import { usePermissions, useUser } from '@/utils/hooks'
import { PERMISSIONS } from '@/utils/permissions'
import { useDebounce } from '@ur-solutions/react-hooks'
import { addDays, formatISO, startOfDay } from 'date-fns'
import { useMemo } from 'react'
import type { InfringementsTableFilter } from '../../types'

/**
 * The filter part of the combined infringement queries. The list and the
 * statistics both use it, so the numbers always describe the rows shown.
 */
export function useCombinedInfringementVariables(
  filter: InfringementsTableFilter
) {
  const me = useUser()
  const { hasPermissions } = usePermissions()
  const canViewOthers = hasPermissions(
    PERMISSIONS.infringements.view.othersinfringement,
    true
  )

  const statuses = useDebounce(filter.statuses)
  const excludeSeverities = useDebounce(filter.excludeSeverities, 500)
  const query = useDebounce(filter.query)

  return useMemo(
    () => ({
      sources: filter.sources ?? ['DRIVING_REST' as const],
      q: query,
      users: canViewOthers ? filter.users : [me.id],
      includeStatuses: statuses,
      excludeSeverities,
      excludeCategories: filter.excludeCategories,
      infringementTimestamp_Gte: formatISO(startOfDay(filter.dateRange.from)),
      infringementTimestamp_Lt: formatISO(
        addDays(startOfDay(filter.dateRange.to), 1)
      ),
      ...(canViewOthers &&
        filter.users.length === 0 &&
        filter.leaderGroup !== null && { leaderGroupId: filter.leaderGroup }),
    }),
    [
      canViewOthers,
      excludeSeverities,
      filter.dateRange.from,
      filter.dateRange.to,
      filter.excludeCategories,
      filter.leaderGroup,
      filter.sources,
      filter.users,
      me.id,
      query,
      statuses,
    ]
  )
}
